import React, { Fragment, useEffect, useRef, useState } from "react";

import AddCircle from "@mui/icons-material/AddCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    Divider,
    FormControl,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import { ButtonVariant } from "Constant/button";
import { Modal } from "react-bootstrap";
import { ClipLoader } from "react-spinners";

import {
    getEvents,
    getSignalSightTaskActions,
    getSignalSightTaskMappings,
    saveSignalSightTaskMappings,
} from "helpers/destinations/createDestination";

import StandartButton from "components/atoms/StandartButton";

// Type definitions
interface ActionParameter {
    name: string;
    code: string;
    type: string;
    required: boolean;
}

interface Action {
    parameters: ActionParameter[];
    code: string;
    name: string;
}

interface ParameterValue {
    value: string;
    required: boolean;
    type: string;
    name: string;
    isDirty?: boolean;
}

interface FieldRow {
    event_name: {
        options: Array<{ label: string; value: string }>;
        isLoading: boolean;
        value: string | null;
    };
    action_code: {
        options: Array<{
            label: string;
            value: string;
            parameters: ActionParameter[];
        }>;
        isLoading: boolean;
        value: string | null;
    };
    parameters: {
        value: Record<string, ParameterValue>;
        isLoading: boolean;
    };
    isExpanded?: boolean;
    id?: string;
}

// Validation error interface
interface ValidationErrors {
    fields?: {
        [rowIndex: number]: {
            event_name?: string;
            action_code?: string;
            parameters?: {
                [paramCode: string]: string;
            };
        };
    };
}

interface SignalSightTaskDestinationMappingProps {
    destinationId: string;
    setButtonDisabledStatus: (disabled: boolean) => void;
    onFieldsChange: (fields: FieldRow[]) => void;
    onSaveSuccess?: () => void;
}

const SignalSightTaskDestinationMapping = ({
    destinationId,
    setButtonDisabledStatus,
    onFieldsChange,
    onSaveSuccess,
}: SignalSightTaskDestinationMappingProps) => {
    const [actions, setActions] = useState<Action[]>([]);
    const [events, setEvents] = useState<
        Array<{ label: string; value: string }>
    >([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [fields, setFields] = useState<FieldRow[]>([]);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
        {},
    );
    const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
    const [focusedRowId, setFocusedRowId] = useState<string | null>(null);

    // Refs for focus management
    const fieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    // Generate unique ID for fields
    const generateFieldId = () =>
        `field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // URL validation function
    const isValidUrl = (url: string): boolean => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    // Validation function to check if form is valid and return errors
    const validateForm = (): { isValid: boolean; errors: ValidationErrors } => {
        const errors: ValidationErrors = {};

        // Check if at least one row exists
        if (fields.length === 0) {
            errors.fields = {
                0: { event_name: "At least one mapping is required" },
            };
            return { isValid: false, errors };
        }

        // Check each row for required fields
        errors.fields = {};
        for (let rowIndex = 0; rowIndex < fields.length; rowIndex++) {
            const row = fields[rowIndex];
            const rowErrors: any = {};

            // Event name is required
            if (!row.event_name.value || row.event_name.value.trim() === "") {
                rowErrors.event_name = "Event is required";
            }

            // Action is required
            if (!row.action_code.value || row.action_code.value.trim() === "") {
                rowErrors.action_code = "Action is required";
            }

            // Check required parameters
            const parameters = row.parameters.value;
            const paramErrors: { [paramCode: string]: string } = {};

            for (const [paramCode, paramData] of Object.entries(parameters)) {
                if (
                    paramData.required &&
                    (!paramData.value || paramData.value.trim() === "")
                ) {
                    paramErrors[paramCode] = `${paramData.name} is required`;
                }

                // URL validation for url type parameters
                if (
                    paramData.type === "url" &&
                    paramData.value &&
                    paramData.value.trim() !== ""
                ) {
                    if (!isValidUrl(paramData.value)) {
                        paramErrors[paramCode] =
                            "Please enter a valid URL (e.g., https://example.com)";
                    }
                }
            }

            if (Object.keys(paramErrors).length > 0) {
                rowErrors.parameters = paramErrors;
            }

            if (Object.keys(rowErrors).length > 0) {
                errors.fields[rowIndex] = rowErrors;
            }
        }
        if (Object.keys(errors.fields).length === 0) {
            delete errors.fields;
        }
        return { isValid: Object.keys(errors).length === 0, errors };
    };

    // Check if a URL parameter is valid
    const isUrlParameterValid = (paramData: ParameterValue): boolean => {
        if (
            paramData.type === "url" &&
            paramData.value &&
            paramData.value.trim() !== ""
        ) {
            return isValidUrl(paramData.value);
        }
        return true;
    };

    // Fetch events and actions on component mount
    useEffect(() => {
        fetchEvents();
        fetchActions();
    }, []);

    // Fetch existing mappings when destinationId is available
    useEffect(() => {
        if (destinationId && actions?.length > 0 && events?.length > 0) {
            fetchMappings();
        }
    }, [actions?.length, events?.length]);

    // Update parent component when fields change
    useEffect(() => {
        onFieldsChange(fields);
    }, [fields]);

    // Update button disabled status based on form validation
    useEffect(() => {
        const { isValid } = validateForm();
        const shouldDisable = hasAttemptedSave && !isValid;
        setButtonDisabledStatus(shouldDisable);
    }, [fields, hasAttemptedSave, setButtonDisabledStatus]);

    const fetchEvents = async () => {
        try {
            const response = await getEvents();
            if (response.status) {
                const eventOptions = response.data?.events?.map(
                    (event: any) => ({
                        label: event.name,
                        value: event.code,
                    }),
                );
                setEvents(eventOptions);
            }
        } catch (error) {
            console.error("Error fetching events:", error);
            setError("Failed to fetch events");
        }
    };

    const fetchActions = async () => {
        try {
            const response = await getSignalSightTaskActions();
            if (response.status) {
                setActions(response.data?.actions);
            }
        } catch (error) {
            console.error("Error fetching actions:", error);
            setError("Failed to fetch actions");
        }
    };

    const fetchMappings = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await getSignalSightTaskMappings(destinationId);
            if (response.status) {
                const mappings = response.data.mappings || [];
                const transformedFields: FieldRow[] = mappings.map(
                    (mapping: any) => {
                        // Find the action to get its parameters
                        const action = actions.find(
                            (a) => a.code === mapping.action,
                        );
                        const parameters: Record<string, ParameterValue> = {};

                        if (action) {
                            action.parameters.forEach((param) => {
                                parameters[param.code] = {
                                    value:
                                        mapping.parameters?.[param.code] || "",
                                    required: param.required,
                                    type: param.type,
                                    name: param.name,
                                    isDirty: !!mapping.parameters?.[param.code],
                                };
                            });
                        }

                        return {
                            event_name: {
                                options: events,
                                isLoading: false,
                                value: mapping.event,
                            },
                            action_code: {
                                options: actions.map((action) => ({
                                    label: action.name,
                                    value: action.code,
                                    parameters: action.parameters,
                                })),
                                isLoading: false,
                                value: mapping.action,
                            },
                            parameters: {
                                value: parameters,
                                isLoading: false,
                            },
                            isExpanded: true,
                            id: generateFieldId(),
                        };
                    },
                );

                setFields(transformedFields);
            } else {
                setError(response.message || "Failed to fetch mappings");
            }
        } catch (error) {
            console.error("Error fetching mappings:", error);
            setError("Failed to fetch mappings");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveMappings = async () => {
        // Set hasAttemptedSave to true to trigger validation for all fields
        setHasAttemptedSave(true);

        // Validate form before proceeding
        const { isValid, errors } = validateForm();
        setValidationErrors(errors);

        // Check if form is valid before proceeding
        if (!isValid) {
            setIsSaving(false);
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const mappings = fields.map((field) => {
                // Transform parameters from complex object to simple key-value pairs
                const transformedParameters = {};
                if (field.parameters?.value) {
                    Object.entries(field.parameters.value).forEach(
                        ([key, paramData]) => {
                            transformedParameters[key] = paramData.value;
                        },
                    );
                }

                return {
                    event: field.event_name?.value,
                    action: field.action_code?.value,
                    parameters: transformedParameters,
                };
            });

            const payload = { mappings };
            const response = await saveSignalSightTaskMappings(
                destinationId,
                payload,
            );

            if (response.status) {
                setSuccessMessage("Mappings saved successfully!");
                // Call onSaveSuccess callback if provided after a brief delay to show success message
                if (onSaveSuccess) {
                    setTimeout(() => {
                        onSaveSuccess();
                    }, 1000);
                }
            } else {
                setError(response.message || "Failed to save mappings");
            }
        } catch (error) {
            console.error("Error saving mappings:", error);
            setError("Failed to save mappings");
        } finally {
            setIsSaving(false);
        }
    };

    const addNewRow = () => {
        const newRow: FieldRow = {
            event_name: {
                options: events,
                isLoading: false,
                value: null,
            },
            action_code: {
                options: actions.map((action) => ({
                    label: action.name,
                    value: action.code,
                    parameters: action.parameters,
                })),
                isLoading: false,
                value: null,
            },
            parameters: {
                value: {},
                isLoading: false,
            },
            isExpanded: true,
            id: generateFieldId(),
        };
        setFields([...fields, newRow]);

        // Focus on the newly added row after a brief delay to ensure DOM is updated
        setTimeout(() => {
            const newRowId = newRow.id;
            if (newRowId && fieldRefs.current[newRowId]) {
                fieldRefs.current[newRowId]?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                // Set focused row for visual highlight
                setFocusedRowId(newRowId);
                // Remove focus highlight after 2 seconds
                setTimeout(() => {
                    setFocusedRowId(null);
                }, 2000);
            }
        }, 100);
    };

    const removeRow = (index: number) => {
        setFields(fields.filter((_, i) => i !== index));

        // Clear validation errors for deleted row
        setValidationErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors.fields && newErrors.fields[index]) {
                delete newErrors.fields[index];
                if (Object.keys(newErrors.fields).length === 0) {
                    delete newErrors.fields;
                }
            }
            return newErrors;
        });
    };

    const handleFieldChange = ({
        rowIndex,
        name,
        value,
    }: {
        rowIndex: number;
        name: string;
        value: string;
    }) => {
        if (name === "action_code" && value) {
            // Find the selected action to get its parameters
            const selectedAction = actions.find(
                (action) => action.code === value,
            );

            if (selectedAction && selectedAction.parameters.length > 0) {
                // Initialize parameters object with default values
                const initialParams = {};
                selectedAction.parameters.forEach((param) => {
                    initialParams[param.code] = {
                        value: "",
                        required: param.required,
                        type: param.type,
                        name: param.name,
                        isDirty: false,
                    };
                });

                setFields(
                    fields?.map((field, index) => {
                        if (rowIndex === index) {
                            field.parameters.value = initialParams;
                            field.isExpanded = true; // Auto-expand when action is selected
                        }
                        return field;
                    }),
                );
            } else {
                // Clear parameters if no action selected or no parameters
                setFields(
                    fields?.map((field, index) => {
                        if (rowIndex === index) {
                            field.parameters.value = {};
                        }
                        return field;
                    }),
                );
            }
        }

        // Update the field value
        setFields(
            fields?.map((field, index) => {
                if (rowIndex === index) {
                    if (name === "event_name") {
                        field.event_name.value = value;
                    } else if (name === "action_code") {
                        field.action_code.value = value;
                    }
                }
                return field;
            }),
        );

        // Clear validation errors for this field when user starts typing
        if (hasAttemptedSave) {
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                if (newErrors.fields && newErrors.fields[rowIndex]) {
                    if (name === "parameters") {
                        delete newErrors.fields[rowIndex].parameters;
                    } else {
                        delete newErrors.fields[rowIndex][name];
                    }
                    if (Object.keys(newErrors.fields[rowIndex]).length === 0) {
                        delete newErrors.fields[rowIndex];
                    }
                }
                if (Object.keys(newErrors.fields || {}).length === 0) {
                    delete newErrors.fields;
                }
                return newErrors;
            });
        }
    };

    // Handle parameter changes
    const handleParameterChange = (
        rowIndex: number,
        paramCode: string,
        value: string,
    ) => {
        setFields(
            fields?.map((field, index) => {
                if (rowIndex === index) {
                    field.parameters.value[paramCode].value = value;
                    field.parameters.value[paramCode].isDirty = true;
                }
                return field;
            }),
        );

        // Clear validation errors for this parameter when user starts typing
        if (hasAttemptedSave) {
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                if (
                    newErrors.fields &&
                    newErrors.fields[rowIndex] &&
                    newErrors.fields[rowIndex].parameters
                ) {
                    delete newErrors.fields[rowIndex].parameters[paramCode];
                    if (
                        Object.keys(newErrors.fields[rowIndex].parameters)
                            .length === 0
                    ) {
                        delete newErrors.fields[rowIndex].parameters;
                    }
                    if (Object.keys(newErrors.fields[rowIndex]).length === 0) {
                        delete newErrors.fields[rowIndex];
                    }
                }
                if (Object.keys(newErrors.fields || {}).length === 0) {
                    delete newErrors.fields;
                }
                return newErrors;
            });
        }
    };

    // Toggle expansion state
    const toggleExpansion = (rowIndex: number) => {
        setFields(
            fields?.map((field, index) => {
                if (rowIndex === index) {
                    field.isExpanded = !field.isExpanded;
                }
                return field;
            }),
        );
    };

    const renderParameters = (
        rowIndex: number,
        parameters: Record<string, ParameterValue>,
    ) => {
        if (!parameters || Object.keys(parameters).length === 0) {
            return (
                <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                        No parameters required for this action
                    </Typography>
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    p: 2,
                    // Mobile responsiveness
                    "@media (max-width: 768px)": {
                        p: 1,
                    },
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: "text.primary",
                        // Mobile responsiveness
                        "@media (max-width: 768px)": {
                            fontSize: "0.9rem",
                            mb: 1,
                        },
                    }}
                >
                    Action Parameters
                </Typography>
                <Stack spacing={2}>
                    {Object.entries(parameters).map(
                        ([paramCode, paramData]) => {
                            const hasError =
                                hasAttemptedSave &&
                                validationErrors.fields?.[rowIndex]
                                    ?.parameters?.[paramCode];

                            return (
                                <TextField
                                    key={paramCode}
                                    label={paramData.name}
                                    variant="outlined"
                                    size="small"
                                    required={paramData.required}
                                    type={
                                        paramData.type === "numeric"
                                            ? "number"
                                            : "text"
                                    }
                                    placeholder={
                                        paramData.type === "url"
                                            ? "https://example.com"
                                            : `Enter ${paramData.name.toLowerCase()}`
                                    }
                                    value={paramData.value || ""}
                                    onChange={(e) =>
                                        handleParameterChange(
                                            rowIndex,
                                            paramCode,
                                            e.target.value,
                                        )
                                    }
                                    error={
                                        !!hasError ||
                                        (paramData.type === "url" &&
                                            paramData.value &&
                                            paramData.value.trim() !== "" &&
                                            !isUrlParameterValid(paramData))
                                    }
                                    helperText={
                                        hasError ||
                                        (paramData.type === "url" &&
                                        paramData.value &&
                                        paramData.value.trim() !== "" &&
                                        !isUrlParameterValid(paramData)
                                            ? "Please enter a valid URL (e.g., https://example.com)"
                                            : paramData.required
                                              ? "This field is required"
                                              : "") ||
                                        ""
                                    }
                                    fullWidth
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            transition: "all 0.2s ease-in-out",
                                            "&:hover": {
                                                "& .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor:
                                                            "primary.main",
                                                    },
                                            },
                                            "&.Mui-focused": {
                                                "& .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor:
                                                            "primary.main",
                                                        borderWidth: 2,
                                                    },
                                            },
                                            "&.Mui-error": {
                                                "& .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor:
                                                            "error.main",
                                                        borderWidth: 2,
                                                    },
                                            },
                                        },
                                        // Mobile responsiveness
                                        "@media (max-width: 768px)": {
                                            "& .MuiOutlinedInput-root": {
                                                fontSize: "0.9rem",
                                            },
                                        },
                                    }}
                                />
                            );
                        },
                    )}
                </Stack>
            </Box>
        );
    };

    if (!destinationId) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <Alert severity="error">
                    No destination ID provided. Cannot load mappings.
                </Alert>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    p: 3,
                    alignItems: "center",
                }}
            >
                <ClipLoader color="#000" size={30} />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading mappings...
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Modal.Body>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        // Mobile responsiveness
                        "@media (max-width: 768px)": {
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 1,
                        },
                        marginBottom: "12px",
                    }}
                >
                    <Chip
                        label={`${fields.length} mapping${
                            fields.length !== 1 ? "s" : ""
                        }`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => {}}
                    />
                </Box>

                <Box
                    className={`custom-scrollbar`}
                    sx={{
                        maxHeight: "30rem",
                        overflowY: "scroll",
                        overflowX: "hidden",
                        // Mobile responsiveness
                        "@media (max-width: 768px)": {
                            maxHeight: "25rem",
                        },
                        "@media (max-width: 480px)": {
                            maxHeight: "20rem",
                        },
                    }}
                >
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {successMessage}
                        </Alert>
                    )}

                    {fields.length > 0 && (
                        <Stack spacing={2}>
                            {fields.map((row, rowIndex) => (
                                <Card
                                    key={row.id || rowIndex}
                                    ref={(el) => {
                                        if (row.id) {
                                            fieldRefs.current[row.id] = el;
                                        }
                                    }}
                                    elevation={0}
                                    sx={{
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 3,
                                        backgroundColor: "#fff",
                                        transition: "all 0.2s ease-in-out",
                                        "&:hover": {
                                            borderColor: "#1976d2",
                                        },
                                        "&:focus-within": {
                                            borderColor: "#1976d2",
                                        },
                                        // Focus highlight for newly added rows
                                        ...(focusedRowId === row.id && {
                                            borderColor: "#1976d2",
                                            backgroundColor: "#f8f9ff",
                                        }),
                                        // Mobile responsiveness
                                        "@media (max-width: 768px)": {
                                            borderRadius: 2,
                                        },
                                    }}
                                >
                                    <CardContent sx={{ p: 0 }}>
                                        {/* Header */}
                                        <Box
                                            sx={{
                                                p: 3,
                                                pb: row.isExpanded ? 2 : 3,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                // Mobile responsiveness
                                                "@media (max-width: 768px)": {
                                                    p: 2,
                                                    pb: row.isExpanded ? 1 : 2,
                                                    flexDirection: "column",
                                                    alignItems: "stretch",
                                                    gap: 2,
                                                },
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                gap={2}
                                                sx={{
                                                    flex: 1,
                                                    // Mobile responsiveness
                                                    "@media (max-width: 768px)":
                                                        {
                                                            flexDirection:
                                                                "column",
                                                            spacing: 1,
                                                            rowGap: 2,
                                                        },
                                                }}
                                            >
                                                {/* Event Selection */}
                                                <FormControl
                                                    sx={{
                                                        minWidth: 200,
                                                        // Mobile responsiveness
                                                        "@media (max-width: 768px)":
                                                            {
                                                                minWidth:
                                                                    "100%",
                                                            },
                                                    }}
                                                >
                                                    <Autocomplete
                                                        freeSolo
                                                        options={
                                                            row.event_name
                                                                .options
                                                        }
                                                        getOptionLabel={(
                                                            option,
                                                        ) => {
                                                            // If option is a string (custom input), return it
                                                            if (
                                                                typeof option ===
                                                                "string"
                                                            ) {
                                                                return option;
                                                            }
                                                            // If option is an object, return its label
                                                            return (
                                                                option.label ||
                                                                ""
                                                            );
                                                        }}
                                                        value={
                                                            row.event_name.options.find(
                                                                (opt) =>
                                                                    opt.value ===
                                                                    row
                                                                        .event_name
                                                                        .value,
                                                            ) ||
                                                            row.event_name
                                                                .value ||
                                                            null
                                                        }
                                                        onChange={(
                                                            event,
                                                            newValue,
                                                        ) => {
                                                            let valueToSet = "";

                                                            // If newValue is an object (selected from dropdown)
                                                            if (
                                                                newValue &&
                                                                typeof newValue ===
                                                                    "object"
                                                            ) {
                                                                valueToSet =
                                                                    newValue.value;
                                                            }
                                                            // If newValue is a string (custom input)
                                                            else if (
                                                                typeof newValue ===
                                                                "string"
                                                            ) {
                                                                valueToSet =
                                                                    newValue;
                                                            }

                                                            handleFieldChange({
                                                                rowIndex,
                                                                name: "event_name",
                                                                value: valueToSet,
                                                            });
                                                        }}
                                                        onInputChange={(
                                                            event,
                                                            newInputValue,
                                                        ) => {
                                                            // This handles the case when user types custom value
                                                            if (
                                                                event?.type ===
                                                                "change"
                                                            ) {
                                                                handleFieldChange(
                                                                    {
                                                                        rowIndex,
                                                                        name: "event_name",
                                                                        value: newInputValue,
                                                                    },
                                                                );
                                                            }
                                                        }}
                                                        renderInput={(
                                                            params,
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                required
                                                                label="Event"
                                                                size="small"
                                                                variant="outlined"
                                                                placeholder="Select or enter custom event"
                                                                error={
                                                                    hasAttemptedSave &&
                                                                    !!validationErrors
                                                                        .fields?.[
                                                                        rowIndex
                                                                    ]
                                                                        ?.event_name
                                                                }
                                                                helperText={
                                                                    hasAttemptedSave &&
                                                                    validationErrors
                                                                        .fields?.[
                                                                        rowIndex
                                                                    ]
                                                                        ?.event_name
                                                                }
                                                                sx={{
                                                                    "& .MuiOutlinedInput-root":
                                                                        {
                                                                            borderRadius: 2,
                                                                            transition:
                                                                                "all 0.2s ease-in-out",
                                                                            "&:hover":
                                                                                {
                                                                                    "& .MuiOutlinedInput-notchedOutline":
                                                                                        {
                                                                                            borderColor:
                                                                                                "primary.main",
                                                                                        },
                                                                                },
                                                                            "&.Mui-focused":
                                                                                {
                                                                                    "& .MuiOutlinedInput-notchedOutline":
                                                                                        {
                                                                                            borderColor:
                                                                                                "primary.main",
                                                                                            borderWidth: 2,
                                                                                        },
                                                                                },
                                                                            "&.Mui-error":
                                                                                {
                                                                                    "& .MuiOutlinedInput-notchedOutline":
                                                                                        {
                                                                                            borderColor:
                                                                                                "error.main",
                                                                                            borderWidth: 2,
                                                                                        },
                                                                                },
                                                                        },
                                                                    // Mobile responsiveness
                                                                    "@media (max-width: 768px)":
                                                                        {
                                                                            "& .MuiOutlinedInput-root":
                                                                                {
                                                                                    fontSize:
                                                                                        "0.9rem",
                                                                                },
                                                                        },
                                                                }}
                                                            />
                                                        )}
                                                        size="small"
                                                        sx={{
                                                            "& .MuiAutocomplete-popupIndicator":
                                                                {
                                                                    color: "primary.main",
                                                                },
                                                        }}
                                                    />
                                                </FormControl>

                                                {/* Action Selection */}
                                                <FormControl
                                                    style={{
                                                        marginLeft: 0,
                                                    }}
                                                    sx={{
                                                        minWidth: 200,
                                                        // Mobile responsiveness
                                                        "@media (max-width: 768px)":
                                                            {
                                                                minWidth:
                                                                    "100%",
                                                            },
                                                    }}
                                                >
                                                    <TextField
                                                        select
                                                        required
                                                        label="Action"
                                                        value={
                                                            row.action_code
                                                                .value || ""
                                                        }
                                                        onChange={(e) =>
                                                            handleFieldChange({
                                                                rowIndex,
                                                                name: "action_code",
                                                                value: e.target
                                                                    .value,
                                                            })
                                                        }
                                                        error={
                                                            hasAttemptedSave &&
                                                            !!validationErrors
                                                                .fields?.[
                                                                rowIndex
                                                            ]?.action_code
                                                        }
                                                        helperText={
                                                            hasAttemptedSave &&
                                                            validationErrors
                                                                .fields?.[
                                                                rowIndex
                                                            ]?.action_code
                                                        }
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            "& .MuiOutlinedInput-root":
                                                                {
                                                                    borderRadius: 2,
                                                                    transition:
                                                                        "all 0.2s ease-in-out",
                                                                    "&:hover": {
                                                                        "& .MuiOutlinedInput-notchedOutline":
                                                                            {
                                                                                borderColor:
                                                                                    "primary.main",
                                                                            },
                                                                    },
                                                                    "&.Mui-focused":
                                                                        {
                                                                            "& .MuiOutlinedInput-notchedOutline":
                                                                                {
                                                                                    borderColor:
                                                                                        "primary.main",
                                                                                    borderWidth: 2,
                                                                                },
                                                                        },
                                                                    "&.Mui-error":
                                                                        {
                                                                            "& .MuiOutlinedInput-notchedOutline":
                                                                                {
                                                                                    borderColor:
                                                                                        "error.main",
                                                                                    borderWidth: 2,
                                                                                },
                                                                        },
                                                                },
                                                            // Mobile responsiveness
                                                            "@media (max-width: 768px)":
                                                                {
                                                                    "& .MuiOutlinedInput-root":
                                                                        {
                                                                            fontSize:
                                                                                "0.9rem",
                                                                        },
                                                                },
                                                        }}
                                                    >
                                                        <MenuItem
                                                            value=""
                                                            disabled
                                                        >
                                                            Select an action
                                                        </MenuItem>
                                                        {row.action_code.options.map(
                                                            (option) => (
                                                                <MenuItem
                                                                    key={
                                                                        option.value
                                                                    }
                                                                    value={
                                                                        option.value
                                                                    }
                                                                >
                                                                    {
                                                                        option.label
                                                                    }
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </TextField>
                                                </FormControl>
                                            </Stack>

                                            {/* Action Buttons */}
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{
                                                    // Mobile responsiveness
                                                    "@media (max-width: 768px)":
                                                        {
                                                            justifyContent:
                                                                "center",
                                                            width: "100%",
                                                        },
                                                }}
                                            >
                                                {row.action_code.value &&
                                                    Object.keys(
                                                        row.parameters.value,
                                                    ).length > 0 && (
                                                        <Tooltip
                                                            title={
                                                                row.isExpanded
                                                                    ? "Hide parameters"
                                                                    : "Show parameters"
                                                            }
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    toggleExpansion(
                                                                        rowIndex,
                                                                    )
                                                                }
                                                                sx={{
                                                                    color: "primary.main",
                                                                    transition:
                                                                        "all 0.2s ease-in-out",
                                                                    "&:hover": {
                                                                        backgroundColor:
                                                                            "primary.light",
                                                                        color: "white",
                                                                        transform:
                                                                            "scale(1.1)",
                                                                    },
                                                                    "&:focus": {
                                                                        backgroundColor:
                                                                            "primary.light",
                                                                        color: "white",
                                                                    },
                                                                }}
                                                            >
                                                                {row.isExpanded ? (
                                                                    <ExpandLessIcon />
                                                                ) : (
                                                                    <ExpandMoreIcon />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                <Tooltip title="Remove mapping">
                                                    <IconButton
                                                        size="small"
                                                        disabled={
                                                            fields.length === 1
                                                        }
                                                        onClick={() =>
                                                            removeRow(rowIndex)
                                                        }
                                                        sx={{
                                                            color: "error.main",
                                                            transition:
                                                                "all 0.2s ease-in-out",
                                                            "&:hover": {
                                                                backgroundColor:
                                                                    "error.light",
                                                                color: "white",
                                                                transform:
                                                                    "scale(1.1)",
                                                            },
                                                            "&:focus": {
                                                                backgroundColor:
                                                                    "error.light",
                                                                color: "white",
                                                            },
                                                            "&.Mui-disabled": {
                                                                color: "text.disabled",
                                                            },
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Box>

                                        {/* Parameters Section */}
                                        {row.action_code.value &&
                                            Object.keys(row.parameters.value)
                                                .length > 0 && (
                                                <Collapse in={row.isExpanded}>
                                                    <Divider />
                                                    {renderParameters(
                                                        rowIndex,
                                                        row.parameters.value,
                                                    )}
                                                </Collapse>
                                            )}
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    )}
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        pt: 2,
                        // Mobile responsiveness
                        "@media (max-width: 768px)": {
                            pt: 1,
                        },
                    }}
                >
                    <StandartButton
                        variant={ButtonVariant.BLACK_OUTLINED}
                        iconButton
                        onClick={addNewRow}
                    >
                        <Stack direction="row" gap={1}>
                            <AddCircle />
                            <Box fontSize="14px" fontWeight={600}>
                                ADD ROW
                            </Box>
                        </Stack>
                    </StandartButton>
                </Box>
            </Modal.Body>
            <Modal.Footer>
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        mb: 3,
                        mt: 2,
                        justifyContent: "flex-end",
                        // Mobile responsiveness
                        "@media (max-width: 768px)": {
                            mb: 2,
                            mt: 1,
                            justifyContent: "center",
                        },
                    }}
                >
                    <Box position="relative">
                        {isSaving && (
                            <div className="circle-loader max-height-45">
                                <ClipLoader color={"#000"} size={17} />
                            </div>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveMappings}
                            sx={{
                                // Mobile responsiveness
                                "@media (max-width: 768px)": {
                                    fontSize: "0.9rem",
                                    padding: "8px 16px",
                                },
                                "@media (max-width: 480px)": {
                                    fontSize: "0.8rem",
                                    padding: "6px 12px",
                                },
                            }}
                        >
                            Save Mappings
                        </Button>
                    </Box>
                </Stack>
            </Modal.Footer>
        </>
    );
};

export default SignalSightTaskDestinationMapping;
